import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Snapshot } from "zephyr-edge-contract"
// Types for Cloudflare API responses
export interface KVNamespace {
  id: string;
  title: string;
  supports_url_encoding: boolean;
}

export interface CloudflareResult<T> {
  result: T;
  success: boolean;
  errors: any[];
  messages: any[];
}

export interface KVKey {
  name: string;
  expiration?: number;
  metadata?: any;
}

export interface KVListResult {
  result: KVKey[];
  success: boolean;
  errors: any[];
  messages: any[];
  result_info: {
    count: number;
    cursor?: string;
  };
}

export interface KVValueResult {
  key: string;
  value: any;
  type: 'json' | 'text' | 'binary';
  size?: number;
  metadata?: any;
}

interface JwtPayload {
  sessionId: string;
  userId: string;
  type: string;
  exp: number;
  iat: number;
}

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

// Store for auth state
interface AuthState {
  accessToken: string;
  refreshToken: string;
  tokenExpiry: number | null;
  isAuthenticated: boolean;
  isRefreshing: boolean;
  setTokens: (
    accessToken: string,
    refreshToken: string,
    expiresIn: number,
  ) => void;
  clearTokens: () => void;
  setRefreshing: (refreshing: boolean) => void;
  checkTokenExpiry: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: '',
      refreshToken: '',
      tokenExpiry: null,
      isAuthenticated: false,
      isRefreshing: false,

      setTokens: (
        accessToken: string,
        refreshToken: string,
        expiresIn: number,
      ) => {
        let tokenExpiry = null;

        // Calculate token expiry time
        try {
          if (accessToken && expiresIn) {
            // Calculate expiry from expiresIn seconds
            tokenExpiry = Date.now() + expiresIn * 1000;
          }
        } catch (error) {
          console.error('Failed to process token expiry:', error);
        }

        set({
          accessToken,
          refreshToken,
          tokenExpiry,
          isAuthenticated: !!accessToken,
        });
      },

      clearTokens: () =>
        set({
          accessToken: '',
          refreshToken: '',
          tokenExpiry: null,
          isAuthenticated: false,
          isRefreshing: false,
        }),

      setRefreshing: (refreshing: boolean) =>
        set({
          isRefreshing: refreshing,
        }),

      // Check if token is expired or about to expire (within 5 minutes)
      checkTokenExpiry: () => {
        const { tokenExpiry } = get();

        if (!tokenExpiry) {
          return false; // No token or invalid token
        }

        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;

        // If token expires in less than 5 minutes or is already expired
        if (tokenExpiry - now < fiveMinutes) {
          return false;
        }

        return true; // Token is valid
      },
    }),
    {
      name: 'auth-storage',
      // Only persist in session storage for security
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          if (!str) return null;
          return JSON.parse(str);
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
    },
  ),
);

interface ZeValueStore {
  namespace: 'ze_snapshots' | 'ze_envs' | 'ze_files' | null;
  key: string | null;
  setKey: (key: string) => void;
  zeValue: Snapshot | null;
  setZeValue: (zeValue: Snapshot) => void;
}

export const useZeValueStore = create<ZeValueStore>()(
  persist(
    (set, get) => ({
      namespace: null,
      key: null,
      zeValue: null,
      setNamespace: (namespace: 'ze_snapshots' | 'ze_envs' | 'ze_files') => set({ namespace }),
      setKey: (key: string) => set({ key }),
      setZeValue: (zeValue: Snapshot) => set({ zeValue }),
      clearZeValue: () => set({ zeValue: null }),
    }),
    {
      name: 'ze-value-storage',
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          if (!str) return null;
          return JSON.parse(str);
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
    },
  ),
);

// Base API client for making requests with authentication
const API_BASE_URL = process.env.NODE_ENV === 'production' ? 'https://bundlercorp.io/api/storage' : 'http://localhost:8787/api/storage';

// Helper function to get headers with authentication
function getAuthHeaders() {
  const { accessToken } = useAuthStore.getState();

  if (!accessToken) {
    throw new Error('Authentication token not found. Please log in.');
  }

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
}

// Helper function to check if token is valid
function isTokenValid(): boolean {
  const { isAuthenticated, checkTokenExpiry } = useAuthStore.getState();

  if (!isAuthenticated) {
    return false;
  }

  return checkTokenExpiry();
}

// Refresh token function
async function refreshAccessToken(): Promise<boolean> {
  const { refreshToken, setTokens, setRefreshing } = useAuthStore.getState();

  if (!refreshToken) {
    return false;
  }

  try {
    // Set refreshing state to prevent multiple refresh attempts
    setRefreshing(true);

    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      // If refresh fails, clear tokens and return false
      useAuthStore.getState().clearTokens();
      return false;
    }

    const data = await response.json();

    if (!data.success || !data.accessToken) {
      useAuthStore.getState().clearTokens();
      return false;
    }

    // Store the new tokens
    setTokens(
      data.accessToken,
      data.refreshToken || refreshToken, // Keep old refresh token if not provided
      data.expiresIn,
    );

    return true;
  } catch (error) {
    console.error('Token refresh error:', error);
    return false;
  } finally {
    setRefreshing(false);
  }
}

// Base fetcher function with authentication and token refresh
async function apiFetch<T>(
  url: string,
  options: RequestInit = {},
  retryOnAuth = true,
): Promise<T> {
  try {
    // Check token validity
    if (!isTokenValid() && retryOnAuth) {
      // Try to refresh the token
      const refreshed = await refreshAccessToken();

      if (!refreshed) {
        throw new Error('Session expired. Please log in again.');
      }

      // Update headers with new token
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${useAuthStore.getState().accessToken}`,
      };
    }

    const response = await fetch(url, options);

    if (response.status === 401 && retryOnAuth) {
      // If unauthorized and we haven't tried refreshing yet
      const refreshed = await refreshAccessToken();

      if (refreshed) {
        // Retry the request with new token
        return apiFetch(
          url,
          {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${useAuthStore.getState().accessToken}`,
            },
          },
          false,
        ); // Don't retry again to prevent infinite loop
      }

      // If we can't refresh, force logout
      useAuthStore.getState().clearTokens();
      throw new Error('Your session has expired. Please log in again.');
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unknown error occurred');
  }
}

// Authentication API
export async function loginApi(
  apiToken: string,
  accountId: string,
): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ apiToken, accountId }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();

    if (!data.success || !data.accessToken) {
      return false;
    }

    // Store the tokens
    useAuthStore
      .getState()
      .setTokens(data.accessToken, data.refreshToken, data.expiresIn);

    return true;
  } catch (error) {
    console.error('Login error:', error);
    return false;
  }
}

export async function logoutApi(): Promise<boolean> {
  try {
    const { accessToken } = useAuthStore.getState();

    if (!accessToken) {
      useAuthStore.getState().clearTokens();
      return true; // Already logged out
    }

    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    // Clear tokens regardless of response
    useAuthStore.getState().clearTokens();

    return response.ok;
  } catch (error) {
    console.error('Logout error:', error);
    // Still clear token on error
    useAuthStore.getState().clearTokens();
    return false;
  }
}

// Cloudflare API
export async function fetchNamespaces(
  prefix?: string,
  cursor?: string,
): Promise<{ namespace: KVNamespace; keys: KVListResult }[]> {
  const namespaces = await apiFetch<CloudflareResult<KVNamespace[]>>(
    `${API_BASE_URL}/namespaces`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    },
  );

  const results = await Promise.all(
    namespaces.result.map(async (namespace) => {
      return {
        namespace,
        keys: await fetchKeys(namespace.id, {
          limit: 20,
          prefix,
          cursor,
        }),
      };
    }),
  );

  return results;
}

export async function fetchKeys(
  namespaceId: string,
  options: {
    prefix?: string;
    cursor?: string;
    limit?: number;
  } = {},
): Promise<KVListResult> {
  const { prefix, cursor, limit } = options;
  const params = new URLSearchParams();

  if (prefix) params.append('prefix', prefix);
  if (cursor) params.append('cursor', cursor);
  if (limit) params.append('limit', limit.toString());

  const url = `${API_BASE_URL}/namespaces/${namespaceId}/keys${params.toString() ? `?${params.toString()}` : ''
    }`;

  return apiFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
}

export async function fetchValue(
  namespaceId: string,
  key: string,
): Promise<KVValueResult> {
  const url = `${API_BASE_URL}/namespaces/${namespaceId}/values/${encodeURIComponent(key)}`;

  const data = await apiFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  console.log('fetchValue', data);

  return data as KVValueResult;
}

// React Query Hooks
export function useNamespaces(prefix?: string, cursor?: string) {
  const { isAuthenticated } = useAuthStore();
  console.log('useNamespaces.isAuthenticated', isAuthenticated);

  return useQuery({
    queryKey: ['namespaces', prefix, cursor],
    queryFn: () => fetchNamespaces(prefix, cursor),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

export function useKeys(
  namespaceId: string,
  options: {
    prefix?: string;
    cursor?: string;
    limit?: number;
  } = {},
) {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['keys', namespaceId, options],
    queryFn: () => fetchKeys(namespaceId, options),
    enabled: isAuthenticated && !!namespaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
    placeholderData: (prev) => prev,
  });
}

export function useKeyValue(namespaceId: string, key: string) {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['keyValue', namespaceId, key],
    queryFn: () => fetchValue(namespaceId, key),
    enabled: isAuthenticated && !!namespaceId && !!key,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

// Auth hooks
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      apiToken,
      accountId,
    }: { apiToken: string; accountId: string }) =>
      loginApi(apiToken, accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['login-namespaces'] });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logoutApi,
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

// Token refresh hook
export function useRefreshToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: refreshAccessToken,
    onSuccess: (success) => {
      if (success) {
        queryClient.invalidateQueries();
      }
    },
  });
}

// Namespace filter store
interface NamespaceFilterState {
  filterZeOnly: boolean;
  toggleZeFilter: () => void;
}

export const useNamespaceFilterStore = create<NamespaceFilterState>()(
  persist(
    (set, get) => ({
      filterZeOnly: false,
      toggleZeFilter: () => set({ filterZeOnly: !get().filterZeOnly }),
    }),
    {
      name: 'namespace-filter-storage',
    },
  ),
);
